package xim.poc.game.configuration.v0.tactics

import xim.math.Vector3f
import xim.poc.ModelLook
import xim.poc.game.actor.components.Inventory
import xim.poc.game.actor.components.InventoryItem
import xim.poc.game.configuration.constants.*
import xim.poc.game.configuration.v0.interactions.ItemId
import xim.poc.game.configuration.v0.tactics.StatRank.A
import xim.poc.game.configuration.v0.tactics.StatRank.B
import xim.poc.game.configuration.v0.tactics.behaviors.*
import xim.poc.ui.ShiftJis
import xim.util.PI_f

typealias TacticsChallengeId = Int

class TacticsChallengeInitialUnit(
    val jobId: TacticsJobId,
    val level: Int,
    val startingTp: Int,
    val position: CursorPosition,
    val unitConfig: TacticsUnitConfig? = null,
    val name: String = tacticsUnitNames.random(),
    val unitRank: StatRank? = null,
)

class TacticsChallengeFlavorArea(
    val zoneId: Int,
    val position: Vector3f,
    val yRotate: Float = 0f,
)

class TacticsChallengeConfig(
    val id: TacticsChallengeId,
    val displayName: String,
    val displayLevel: Int,
    val initialUnits: List<TacticsChallengeInitialUnit>,
    val requirements: List<TacticsChallengeId> = emptyList(),
    val bonusText: String? = null,
    val bonusDetails: String? = null,
    val musicId: Int? = null,
    val flavorArea: TacticsChallengeFlavorArea,
    val itemChallenge: ItemId? = null,
)

object TacticsChallengeConfigs {

    private val challenges: Map<TacticsChallengeId, TacticsChallengeConfig>

    init {
        val c = ArrayList<TacticsChallengeConfig>()

        c += TacticsChallengeConfig(id = tacticsChallenge_Emissary, displayName = "The Emissary", displayLevel = 5, musicId = 125,
            flavorArea = TacticsChallengeFlavorArea(zoneId = 146, position = Vector3f(x=421f,y=53f,z=424f), yRotate = PI_f),
            bonusText = "Inheritance",
            bonusDetails = "Units can now equip ${ShiftJis.colorAug}\"All Job\" Monster Skills${ShiftJis.colorClear} learned by other units via the '${ShiftJis.colorWhite}Manage Units${ShiftJis.colorClear}' menu.",
            initialUnits = listOf(
                TacticsChallengeInitialUnit(name = "Dark Dragon", jobId = tacticsJob_Dragon, unitRank = B, level = 5, startingTp = 700, position = PanelPosition(2, 3),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        xpMultiplier = 3f,
                        hpMultiplier = 1.5f,
                        skillOverride = listOf(mskillFlameBreath_386, mskillHeavyStomp_390, mskillDiscordantNote_3507),
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Emissary, 0)) { TacticsDarkDragonBehavior(it) }
                    ),
                ),
                TacticsChallengeInitialUnit(name = "Seeker", jobId = tacticsJob_Ahriman, level = 5, startingTp = 800, position = PanelPosition(1, 4),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        xpMultiplier = 0f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Emissary, 1)) { TacticsSeekerBehavior(it) }
                    ),
                ),
            ),
        )

        c += TacticsChallengeConfig(id = tacticsChallenge_Frontier, displayName = "The Frontier", displayLevel = 10, musicId = 62,
            flavorArea = TacticsChallengeFlavorArea(zoneId = 261, position = Vector3f(x=-145.81f,y=-7.43f,z=412.43f)),
            bonusText = "C-Rank",
            bonusDetails = "Starting from ${ShiftJis.colorAug}Unit Level 10${ShiftJis.colorClear}, units have the option to ${ShiftJis.colorAug}Rank Up${ShiftJis.colorClear} or ${ShiftJis.colorAug}Change Jobs${ShiftJis.colorClear} via the '${ShiftJis.colorWhite}Manage Units${ShiftJis.colorClear}' menu.",
            requirements = listOf(tacticsChallenge_Emissary),
            initialUnits = listOf(
                TacticsChallengeInitialUnit(name = "Colkhab", jobId = tacticsJob_Bztavian, unitRank = B, level = 10, startingTp = 800, position = PanelPosition(2, 3),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        xpMultiplier = 3f,
                        hpMultiplier = 1.5f,
                        skillOverride = listOf(spellAeroII_155, spellAerogaII_185, mskillMandibularLashing_2746, mskillVespineHurricane_2747, mskillStingerVolley_2748, mskillDroningWhirlwind_2749, mskillIncisiveDenouement_2750, mskillIncisiveApotheosis_2751),
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Frontier, 0)) { TacticsColkhabBehavior(it) }
                    ),
                ),
            ),
        )

        c += TacticsChallengeConfig(id = tacticsChallenge_AncientVows, displayName = "Ancient Vows", displayLevel = 15, musicId = 220,
            flavorArea = TacticsChallengeFlavorArea(zoneId = 31, position = Vector3f(x=-0.42f,y=-3.00f,z=-6.00f)),
            bonusText = "Instinct +1",
            bonusDetails = "Units can now have up to ${ShiftJis.colorAug}4 instincts${ShiftJis.colorClear}. ${ShiftJis.colorWhite}${ShiftJis.leftRoundedBracket}Instinct Roller${ShiftJis.rightRoundedBracket}${ShiftJis.colorClear} has appeared!",
            requirements = listOf(tacticsChallenge_Frontier),
            initialUnits = listOf(
                TacticsChallengeInitialUnit(name = "Mammet Alpha", jobId = tacticsJob_MammetSword, level = 16, startingTp = 800, position = PanelPosition(1, 3),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 1.2f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_AncientVows, 0)) { TacticsMammetBehavior(MammetForm.Sword, it) }
                    ),
                ),
                TacticsChallengeInitialUnit(name = "Mammet Beta", jobId = tacticsJob_MammetSpear, level = 16, startingTp = 800, position = PanelPosition(2, 4),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 1.2f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_AncientVows, 1)) { TacticsMammetBehavior(MammetForm.Spear, it) }
                    ),
                ),
                TacticsChallengeInitialUnit(name = "Mammet Gamma", jobId = tacticsJob_MammetStaff, level = 16, startingTp = 800, position = PanelPosition(3, 3),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 1.2f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_AncientVows, 2)) { TacticsMammetBehavior(MammetForm.Staff, it) },
                    ),
                ),
            ),
        )

        val shantottoClones = AvatarConfig.values().mapIndexed { i, avatar ->
            val position = if (i == 0) { EnemyPrepPosition(1, 0) } else { EnemyPrepPosition(0, i - 1) }
            TacticsChallengeInitialUnit(name = "Clone", jobId = tacticsJob_ShantottoClone, level = 20, startingTp = 900 - 180 * i, position = position,
                unitConfig = TacticsUnitConfig(
                    bossUnit = true,
                    behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_BattaruRoyale, 10 + i)) { TacticsShantottoClone(avatar, it) },
                    skillOverride = listOf(skillFullSwing_182, skillRetribution_184, avatar.summonSkill, avatar.astralFlow),
                ),
            )
        }

        c += TacticsChallengeConfig(id = tacticsChallenge_BattaruRoyale, displayName = "Battaru Royale", displayLevel = 20, musicId = 50,
            flavorArea = TacticsChallengeFlavorArea(zoneId = 170, position = Vector3f(x=339.00f,y=43.50f,z=-360.00f)),
            bonusText = "Idle XP/JP",
            bonusDetails = "Non-deployed units will gain ${ShiftJis.colorAug}XP & JP${ShiftJis.colorClear} as well!",
            requirements = listOf(tacticsChallenge_AncientVows),
            initialUnits = listOf(
                TacticsChallengeInitialUnit(name = "Shantotto", jobId = tacticsJob_ShantottoII, level = 20, startingTp = 800, position = PanelPosition(2, 3),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        xpMultiplier = 3f,
                        hpMultiplier = 1.25f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_BattaruRoyale, 0)) { TacticsShantottoBehavior(it) },
                    ),
                ),
            ) + shantottoClones,
        )

        c += TacticsChallengeConfig(id = tacticsChallenge_Nought, displayName = "All for Nought", displayLevel = 25, musicId = 85,
            flavorArea = TacticsChallengeFlavorArea(zoneId = 183, position = Vector3f(x=-18.86f,y=8.00f,z=19.07f)),
            requirements = listOf(tacticsChallenge_BattaruRoyale),
            bonusText = "B-Rank",
            bonusDetails = "Starting from ${ShiftJis.colorAug}Unit Level 25${ShiftJis.colorClear}, units unlock a new ${ShiftJis.colorAug}Rank${ShiftJis.colorClear} and ${ShiftJis.colorAug}Job${ShiftJis.colorClear}.",
            initialUnits = listOf(
                TacticsChallengeInitialUnit(name = "Blazenought", jobId = tacticsJobId_Blazenought, unitRank = A, level = 25, startingTp = 600, position = PanelPosition(1, 4),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 1.685f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Nought, 0)) { TacticsIroncladBehavior(it) },
                    ),
                ),
                TacticsChallengeInitialUnit(name = "Frostnought", jobId = tacticsJobId_Frostnought, unitRank = A, level = 25, startingTp = 600, position = PanelPosition(3, 4),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 1.685f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Nought, 0)) { TacticsIroncladBehavior(it) },
                    ),
                ),
            ),
        )

        c += TacticsChallengeConfig(id = tacticsChallenge_Remnants, displayName = "Remnants", displayLevel = 30, musicId = 148,
            flavorArea = TacticsChallengeFlavorArea(zoneId = 73, position = Vector3f(x=259.99f,y=-4.00f,z=46.00f)),
            requirements = listOf(tacticsChallenge_Nought),
            initialUnits = listOf(
                TacticsChallengeInitialUnit(name = "Chariot", jobId = tacticsJobId_DiscoidChariot, unitRank = A, level = 30, startingTp = 800, position = EnemyPrepPosition(0, 4),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        xpMultiplier = 3f,
                        hpMultiplier = 3f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Remnants, 0)) { TacticsDiscoidBehavior(it) },
                    ),
                ),
                TacticsChallengeInitialUnit(name = "Rampart A", jobId = tacticsJobId_Rampart, unitRank = A, level = 30, startingTp = 1000, position = PanelPosition(1, 4),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 1.25f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Remnants, 1)) { TacticsRampartBehavior(it) },
                    ),
                ),
                TacticsChallengeInitialUnit(name = "Rampart B", jobId = tacticsJobId_Rampart, unitRank = A, level = 30, startingTp = 1000, position = PanelPosition(3, 4),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 1.25f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Remnants, 1)) { TacticsRampartBehavior(it) },
                    ),
                ),
            ),
        )

        c += TacticsChallengeConfig(id = tacticsChallenge_Disjoined, displayName = "Both Paths Taken", displayLevel = 35, musicId = 224,
            flavorArea = TacticsChallengeFlavorArea(zoneId = 36, position = Vector3f(x=-520.00f,y=-121.00f,z=520.00f)),
            requirements = listOf(tacticsChallenge_Remnants),
            bonusText = "Rings",
            bonusDetails = "${ShiftJis.colorItem}Rings${ShiftJis.colorClear} can be purchased from the Item Shop!",
            initialUnits = listOf(
                TacticsChallengeInitialUnit(name = "Disjoined One", jobId = tacticsJobId_DisjoinedOne, unitRank = A, level = 35, startingTp = 800, position = PanelPosition(2, 3),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        xpMultiplier = 3f,
                        hpMultiplier = 4f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Disjoined, 0)) { TacticsDisjoinedBehavior(it) },
                    ),
                ),
                TacticsChallengeInitialUnit(name = "Selh'teus", jobId = tacticsJobId_Selhteus, unitRank = A, level = 35, startingTp = 800, position = PanelPosition(2, 4),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 2f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Disjoined, 1)) { TacticsSelhteusBehavior(it) },
                    ),
                ),
                TacticsChallengeInitialUnit(name = "Slime", jobId = tacticsJobId_Slash, unitRank = B, level = 35, startingTp = 800, position = PanelPosition(1, 4),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Disjoined, 2)) { TacticsSlashBehavior(it, ModelLook.npc(0xB41)) },
                        skillOverride = listOf(mskillFrizz_2871, mskillMegaflare_1295)
                    ),
                ),
            )
        )

        c += TacticsChallengeConfig(id = tacticsChallenge_Metus, displayName = "Winds of the Future", displayLevel = 35, musicId = 137,
            flavorArea = TacticsChallengeFlavorArea(zoneId = 255, position = Vector3f(x=520.53f,y=-500.00f,z=522.76f)),
            requirements = listOf(tacticsChallenge_Disjoined),
            initialUnits = listOf(
                TacticsChallengeInitialUnit(name = "Metus", jobId = tacticsJobId_Metus, unitRank = StatRank.S, level = 35, startingTp = 600, position = PanelPosition(2, 3),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        xpMultiplier = 3f,
                        hpMultiplier = 5.367f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Metus, 0)) { TacticsMetusBehavior(it) },
                        inventory = Inventory().also { it.addItem(armorGyveDoublet_25708) }
                    ),
                ),
            )
        )

        c += TacticsChallengeConfig(id = tacticsChallenge_Fenrir, displayName = "Fenrir Prime", displayLevel = 32, musicId = 193, itemChallenge = itemNightEarring_13349,
            flavorArea = TacticsChallengeFlavorArea(zoneId = 170, position = Vector3f(x=339.00f,y=43.50f,z=-360.00f)),
            initialUnits = listOf(
                TacticsChallengeInitialUnit(name = "Fenrir Prime", jobId = tacticsJobId_Fenrir, unitRank = A, level = 35, startingTp = 800, position = PanelPosition(2, 3),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 4.25f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Fenrir, 0)) { TacticsFenrirBehavior(it) },
                    ),
                ),
            )
        )

        c += TacticsChallengeConfig(id = tacticsChallenge_Carbuncle, displayName = "Carbuncle Prime", displayLevel = 32, musicId = 193, itemChallenge = itemMoonEarring_13350,
            flavorArea = TacticsChallengeFlavorArea(zoneId = 170, position = Vector3f(x=339.00f,y=43.50f,z=-360.00f)),
            initialUnits = listOf(
                TacticsChallengeInitialUnit(name = "Carbuncle Prime", jobId = tacticsJobId_Carbuncle, unitRank = A, level = 35, startingTp = 800, position = PanelPosition(2, 4),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 2.25f,
                        xpMultiplier = 10f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Carbuncle, 0)) { TacticsCarbuncleBehavior(it) },
                        skillOverride = listOf(mskillPoisonNails_651, mskillMeteorite_654, mskillSearingLight_656, mskillHolyMist_3297)
                    ),
                ),
                TacticsChallengeInitialUnit(name = "Carbuncle Alpha", jobId = tacticsJobId_Carbuncle, unitRank = A, level = 35, startingTp = 1000, position = PanelPosition(1, 3),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 1.75f,
                        xpMultiplier = 0f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Carbuncle, 1)) { TacticsSupportCarbuncleBehavior(it) },
                        skillOverride = listOf(mskillPoisonNails_651, mskillGlitteringRuby_653, mskillMeteorite_654, mskillHealingRubyII_655)
                    ),
                ),
                TacticsChallengeInitialUnit(name = "Carbuncle Beta", jobId = tacticsJobId_Carbuncle, unitRank = A, level = 35, startingTp = 1000, position = PanelPosition(3, 3),
                    unitConfig = TacticsUnitConfig(
                        bossUnit = true,
                        hpMultiplier = 1.75f,
                        xpMultiplier = 0f,
                        behavior = TacticsUnitBehavior(TacticsChallengeBehaviorId(tacticsChallenge_Carbuncle, 2)) { TacticsSupportCarbuncleBehavior(it) },
                        skillOverride = listOf(mskillPoisonNails_651, mskillShiningRuby_652, mskillMeteorite_654, mskillHealingRubyII_655)
                    ),
                ),
            )
        )

        if (c.distinctBy { it.id }.size != c.size) { throw IllegalStateException("Not unique IDs!") }
        challenges = c.associateBy { it.id }
    }

    operator fun get(id: TacticsChallengeId): TacticsChallengeConfig {
        return challenges[id] ?: throw IllegalStateException("No such challenge: $id")
    }

    operator fun get(item: InventoryItem): TacticsChallengeConfig? {
        return challenges.values.firstOrNull { it.itemChallenge == item.id }
    }

    fun getAll(): List<TacticsChallengeConfig> {
        return challenges.values.sortedBy { it.id }.filter { !it.isItemChallenge() }
    }

}

fun TacticsChallengeConfig.isUnlocked(): Boolean {
    return requirements.all { TacticsState.hasClearedChallenge(it) }
}

fun TacticsChallengeConfig.isItemChallenge(): Boolean {
    return itemChallenge != null
}


const val tacticsChallenge_Emissary = 1
const val tacticsChallenge_Frontier = 2
const val tacticsChallenge_AncientVows = 3
const val tacticsChallenge_BattaruRoyale = 4
const val tacticsChallenge_Nought = 5
const val tacticsChallenge_Remnants = 6
const val tacticsChallenge_Disjoined = 7
const val tacticsChallenge_Metus = 8

const val tacticsChallenge_Fenrir = 100
const val tacticsChallenge_Carbuncle = 101